import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import type { Schemas } from "@/api/client";
import { useAuth } from "@/app/auth";
import { LogoMark } from "@/components/common/Logo";

const securityPath = "/settings/security";

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
        <LogoMark className="size-10 animate-pulse" />
      </div>
    );
  }
  if (auth.status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  // Company policy: staff without 2FA may only turn it on.
  if (auth.me.mfa_setup_required && location.pathname !== securityPath) {
    return <Navigate to={securityPath} replace />;
  }
  return children;
}

type Permission = keyof Schemas["Permissions"];

/** Shows the page only with the permission; the server checks access anyway. */
export function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const auth = useAuth();
  if (auth.status !== "authenticated" || !auth.me.permissions[permission]) return <Navigate to="/" replace />;
  return children;
}
